import React, { useState, useEffect, useMemo } from "react";
import { getDocuments, updateUserSettings } from "../utils/firestoreService";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../utils/firebase";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Pagination from "@mui/material/Pagination";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import { Search, Plus, Edit, MessageSquare, HelpCircle, Copy } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ModalPortal from "../utils/ModalPortal";
import { useAdminSessionState } from './useAdminSessionState';
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";

const PROMPTS_PER_PAGE = 100;

const AdminPrompts = ({ adminPrefs, setAdminPrefs, userId }) => {
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useAdminSessionState(`admin:${userId}:promptsSearch`, "");
  const [currentPage, setCurrentPage] = useAdminSessionState(`admin:${userId}:promptsPage`, 1);
  const [sortConfig, setSortConfig] = useState({ 
    key: adminPrefs?.promptsSortKey || 'name', 
    direction: adminPrefs?.promptsSortDirection || 'asc' 
  });
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [promptToClone, setPromptToClone] = useState(null);
  const [cloneNewName, setCloneNewName] = useState("");
  const [cloneError, setCloneError] = useState("");
  const [isCloning, setIsCloning] = useState(false);

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const promptsData = await getDocuments('prompts');
      setPrompts(promptsData);
    } catch (error) {
      console.error("Error fetching prompts:", error);
    }
    setIsLoading(false);
  };

  const handleRequestSort = (property) => {
    const isAsc = sortConfig.key === property && sortConfig.direction === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortConfig({ key: property, direction: newDirection });
    // Persist sort preferences
    if (userId && setAdminPrefs) {
      const updatedPrefs = { ...adminPrefs, promptsSortKey: property, promptsSortDirection: newDirection };
      setAdminPrefs(updatedPrefs);
      updateUserSettings(userId, { adminPreferences: updatedPrefs });
    }
  };

  const handleNewPrompt = () => {
    navigate('/admin/prompts/new');
  };

  const handleEditPrompt = (prompt) => {
    navigate(`/admin/prompts/edit/${prompt.id}`);
  };

  const handleClone = (prompt) => {
    setPromptToClone(prompt);
    setCloneNewName(`${prompt.name || 'Untitled'} Copy`);
    setCloneError("");
    setCloneDialogOpen(true);
  };

  const confirmClone = async () => {
    if (!promptToClone || !cloneNewName.trim()) return;
    
    // Check if name already exists
    const existingNames = prompts.map(p => (p.name || '').toLowerCase());
    if (existingNames.includes(cloneNewName.trim().toLowerCase())) {
      setCloneError("A prompt with this name already exists");
      return;
    }
    
    setIsCloning(true);
    setCloneError("");
    try {
      // Clone the prompt, reset versioning
      const { id, createdAt, updatedAt, updatedBy, versions, version, publishedVersion, ...rest } = promptToClone;
      
      const clonedData = {
        ...rest,
        name: cloneNewName.trim(),
        version: 1,
        versions: [],
        publishedVersion: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };
      
      // Use auto-generated ID for prompts
      const newDocRef = doc(db, 'prompts', crypto.randomUUID());
      await setDoc(newDocRef, clonedData);
      
      setCloneDialogOpen(false);
      setPromptToClone(null);
      setCloneNewName("");
      fetchPrompts();
    } catch (error) {
      console.error("Error cloning prompt:", error);
      setCloneError("Failed to clone: " + error.message);
    }
    setIsCloning(false);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    });
  };

  const filteredPrompts = prompts.filter(prompt => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      prompt.name?.toLowerCase().includes(query) ||
      prompt.type?.toLowerCase().includes(query) ||
      prompt.model?.toLowerCase().includes(query)
    );
  });

  const sortedPrompts = useMemo(() => {
    const sorted = [...filteredPrompts].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = (a.name || 'Untitled').toLowerCase();
          bValue = (b.name || 'Untitled').toLowerCase();
          break;
        case 'type':
          aValue = (a.type || '').toLowerCase();
          bValue = (b.type || '').toLowerCase();
          break;
        case 'model':
          aValue = (a.model || '').toLowerCase();
          bValue = (b.model || '').toLowerCase();
          break;
        case 'version':
          aValue = a.version || 1;
          bValue = b.version || 1;
          break;
        case 'updatedAt':
          aValue = a.updatedAt?.toDate ? a.updatedAt.toDate().getTime() : (a.updatedAt?.seconds ? a.updatedAt.seconds * 1000 : 0);
          bValue = b.updatedAt?.toDate ? b.updatedAt.toDate().getTime() : (b.updatedAt?.seconds ? b.updatedAt.seconds * 1000 : 0);
          break;
        default:
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredPrompts, sortConfig]);

  // Pagination calculations
  const indexOfLastPrompt = currentPage * PROMPTS_PER_PAGE;
  const indexOfFirstPrompt = indexOfLastPrompt - PROMPTS_PER_PAGE;
  const currentPrompts = sortedPrompts.slice(indexOfFirstPrompt, indexOfLastPrompt);
  const totalPages = Math.ceil(sortedPrompts.length / PROMPTS_PER_PAGE);

  useEffect(() => {
    if (isLoading) return;
    setCurrentPage(page => Math.min(page, Math.max(1, totalPages)));
  }, [isLoading, totalPages, setCurrentPage]);

  return (
    <div className="standard-container" style={{ marginBottom: '60px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div className="home-header-search-wrapper" style={{ width: 'auto' }}>
            <Tooltip title="Search prompts" arrow>
              <Search className="header-search-icon" size={18} />
            </Tooltip>
            <input
              type="text"
              placeholder="Search Prompts"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="header-search-input"
            />
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Help" arrow>
            <IconButton onClick={() => setHelpDialogOpen(true)} sx={{ mr: 1 }}>
              <HelpCircle size={20} />
            </IconButton>
          </Tooltip>
          <Button
            startIcon={<Plus size={18} />}
            onClick={handleNewPrompt}
            variant="contained"
            color="primary"
          >
            Create Prompt
          </Button>
        </div>
      </div>

      {/* Help Dialog */}
      <ModalPortal isOpen={helpDialogOpen}>
        <Dialog 
          open={helpDialogOpen} 
          onClose={() => setHelpDialogOpen(false)} 
          maxWidth="sm" 
          fullWidth
          sx={{ zIndex: 'var(--z-modal-backdrop)' }}
        >
          <DialogTitle>Prompts Help</DialogTitle>
          <DialogContent dividers>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Table Columns</Typography>
            <Typography variant="body2" component="div" paragraph>
              <strong>Type</strong> — Categorizes the prompt (e.g., extraction, formatting, summary).<br /><br />
              <strong>Model</strong> — Flash models are faster; Pro models handle complex tasks better.<br /><br />
              <strong>Version</strong> — Current version number. Each save creates a new version.<br /><br />
              <strong>Status</strong> — Active prompts are available for document processing. Inactive prompts are hidden.
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Tips</Typography>
            <Typography variant="body2" component="div" paragraph>
              • Test prompts with sample transcripts before using in production<br />
              • Deactivate rather than delete if unsure about a prompt<br />
              • Version history is preserved — iterate freely
            </Typography>

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setHelpDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>

      {isLoading && (
        <Box className="table-loading-bar">
          <LinearProgress />
        </Box>
      )}

      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid var(--color-border)' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'var(--color-background-alt)' }}>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortConfig.key === 'name'}
                  direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('name')}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortConfig.key === 'type'}
                  direction={sortConfig.key === 'type' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('type')}
                >
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortConfig.key === 'model'}
                  direction={sortConfig.key === 'model' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('model')}
                >
                  Model
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                <TableSortLabel
                  active={sortConfig.key === 'version'}
                  direction={sortConfig.key === 'version' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('version')}
                >
                  Version
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>
                <TableSortLabel
                  active={sortConfig.key === 'updatedAt'}
                  direction={sortConfig.key === 'updatedAt' ? sortConfig.direction : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Updated
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPrompts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  {isLoading ? (
                    "Loading..."
                  ) : searchQuery ? (
                    "No prompts match your search"
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <MessageSquare size={48} color="var(--color-text-secondary)" />
                      <span>No prompts found. Create your first prompt to get started.</span>
                      <Button
                        startIcon={<Plus size={16} />}
                        onClick={handleNewPrompt}
                        variant="outlined"
                        size="small"
                      >
                        Create First Prompt
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              currentPrompts.map((prompt) => (
                <TableRow 
                  key={prompt.id}
                  sx={{ '&:hover': { backgroundColor: 'var(--color-background-alt)' } }}
                >
                  <TableCell>
                    <span 
                      className="table-primary-text" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleEditPrompt(prompt)}
                    >
                      {prompt.name || 'Untitled Prompt'}
                    </span>
                  </TableCell>
                  <TableCell>
                    {prompt.type && (
                      <Chip 
                        label={prompt.type} 
                        size="small" 
                        variant="outlined" 
                        color="primary"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="patient-details-small">
                      {prompt.model || 'gemini-2.5-flash'}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <span className="patient-details-small">
                      v{prompt.version || 1}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    {prompt.publishedVersion ? (
                      <Chip 
                        label={prompt.publishedVersion === prompt.version ? "Published" : "Draft"} 
                        size="small" 
                        color={prompt.publishedVersion === prompt.version ? "success" : "warning"}
                        sx={{ fontSize: 'var(--text-xs)', height: '20px' }}
                      />
                    ) : (
                      <Chip 
                        label="Unpublished" 
                        size="small" 
                        variant="outlined"
                        sx={{ fontSize: 'var(--text-xs)', height: '20px', opacity: 0.6 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="patient-details-small">
                      {formatDate(prompt.updatedAt)}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit prompt" arrow>
                      <IconButton
                        onClick={() => handleEditPrompt(prompt)}
                        size="small"
                        color="primary"
                      >
                        <Edit size={16} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Clone prompt" arrow>
                      <IconButton
                        onClick={() => handleClone(prompt)}
                        size="small"
                      >
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
          Showing {filteredPrompts.length > 0 ? indexOfFirstPrompt + 1 : 0}-{Math.min(indexOfLastPrompt, filteredPrompts.length)} of {filteredPrompts.length} prompts
          {searchQuery && ` (filtered from ${prompts.length})`}
        </span>
        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            variant="outlined"
            shape="rounded"
            size="small"
          />
        )}
      </div>

      {/* Clone Dialog */}
      <ModalPortal isOpen={cloneDialogOpen}>
        <Dialog
          open={cloneDialogOpen}
          onClose={() => !isCloning && setCloneDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 'var(--z-modal-backdrop)' }}
        >
          <DialogTitle>Clone Prompt</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Create a copy of "{promptToClone?.name || 'this prompt'}" with a new name.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="New Prompt Name"
              value={cloneNewName}
              onChange={(e) => setCloneNewName(e.target.value)}
              error={!!cloneError}
              helperText={cloneError}
              disabled={isCloning}
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCloneDialogOpen(false)} disabled={isCloning}>
              Cancel
            </Button>
            <Button
              onClick={confirmClone}
              variant="contained"
              disabled={isCloning || !cloneNewName.trim()}
              startIcon={isCloning ? <CircularProgress size={16} /> : null}
            >
              {isCloning ? "Cloning..." : "Clone"}
            </Button>
          </DialogActions>
        </Dialog>
      </ModalPortal>
    </div>
  );
};

export default AdminPrompts;
